import { IllustrationOpeningAnimation } from "@assets/IllustrationOpeningAnimation";
import type { DoorUnlockOutcome } from "@components/Polling/DoorUnlockPolling";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { DoorClosingScreen } from "~/components/DoorClosingScreen";
import { DoorOpeningScreen } from "~/components/DoorOpeningScreen";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";

// Renders nothing; calls onDone after a short delay. Used as a fallback opening
// animation duration when we don't have an unlock request id to poll.
function OpeningFallbackTimer(props: { onDone: () => void }) {
	const { onDone } = props;
	useEffect(() => {
		const timer = setTimeout(onDone, 2500);
		return () => clearTimeout(timer);
	}, [onDone]);
	return null;
}

type OpeningOverlayProps = {
	service: "LOCK_B2B" | "LOCK_B2C";
	unlockRequestId: string | undefined;
	accessToken?: string;
	isSubmitting?: boolean;
	onTerminate: (outcome: "success" | "timed_out" | "error") => void;
	showHeader?: boolean;
	closingTitle?: string;
	closingDescription?: string;
	closingFooter?: ReactNode;
};

export function OpeningOverlay(props: OpeningOverlayProps) {
	const {
		service,
		unlockRequestId,
		accessToken,
		isSubmitting = false,
		onTerminate,
		showHeader = true,
		closingTitle,
		closingDescription,
		closingFooter,
	} = props;
	const i18n = useI18nContext().LL["new-ui"];
	const [phase, setPhase] = useState<"opening" | "closing">("opening");
	const handleTerminate = (outcome: DoorUnlockOutcome) => {
		const mappedOutcome = outcome.type === "closed" ? "success" : outcome.type === "timed_out" ? "timed_out" : "error";
		if (outcome.type === "timed_out") toast.error(i18n.DOOR_NOT_OPENED_IN_TIME());
		if (outcome.type === "error") toast.error(i18n.SOMETHING_WENT_WRONG());
		onTerminate(mappedOutcome);
	};

	if (unlockRequestId) {
		if (phase === "closing") {
			return (
				<DoorClosingScreen
					header={showHeader ? <AppHeader service={service} showHelpButton={true} /> : null}
					unlockRequestId={unlockRequestId}
					accessToken={accessToken}
					title={closingTitle}
					description={closingDescription}
					footer={closingFooter}
					onClosed={() => handleTerminate({ type: "closed" })}
				/>
			);
		}

		return (
			<DoorOpeningScreen
				header={showHeader ? <AppHeader service={service} showHelpButton={true} /> : null}
				unlockRequestId={unlockRequestId}
				accessToken={accessToken}
				onOpened={() => setPhase("closing")}
				onClosed={() => handleTerminate({ type: "closed" })}
			/>
		);
	}

	return (
		<PageContainer className="px-6">
			{!isSubmitting && <OpeningFallbackTimer onDone={() => handleTerminate({ type: "error" })} />}
			{showHeader ? (
				<div className="-mx-6">
					<AppHeader service={service} showHelpButton={true} />
				</div>
			) : null}
			<h1 className="text-2xl font-breit">
				<T>{i18n.OPENING_DOOR_FOR_YOU()}</T>
			</h1>
			<p className="text-sm text-muted-foreground">
				<T>{i18n.OPENING_DOOR_DESC()}</T>
			</p>
			<div className="flex h-[468px] items-center justify-center">
				<IllustrationOpeningAnimation className="w-[163px]" />
			</div>
		</PageContainer>
	);
}
